import { useState, useEffect } from "react";
import MyTable from "../../Components/MyTable";
import Mytitle from "../../Components/Mytitle";
import ModalForm from "../../Components/ModalForm";
import API from "../../Api/axiosConfig";
import { useTranslation } from "react-i18next";
import { FloatingLabel, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faCar, faFileInvoiceDollar, faBuilding, faRightLeft } from "@fortawesome/free-solid-svg-icons";
import Can from "../../Components/Can";
import { toLocal } from "../../utils/toLocal";

function CarInventoryReport() {
  const { t } = useTranslation();

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  // حالات مودال العرض
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);

  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferCar, setTransferCar] = useState(null);
  const [transferBranchId, setTransferBranchId] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState("");

  // Load branches
  useEffect(() => {
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      const response = await API.get("/branches");
      setBranches(response.data.data || []);
    } catch (err) {
      console.error("Failed to load branches", err);
    }
  };

  const handleBranchChange = (branchId) => {
    setSelectedBranch(branchId);
    setRefreshKey((prev) => prev + 1);
  };

  const handleView = (record) => {
    setSelectedCar(record);
    setShowViewModal(true);
  };

  const handleTransferOpen = (record) => {
    setTransferCar(record);
    setTransferBranchId("");
    setTransferError("");
    setShowTransferModal(true);
  };

  const handleTransferSubmit = async () => {
    if (!transferBranchId) {
      setTransferError(t("please_select_branch"));
      return;
    }
    setTransferLoading(true);
    setTransferError("");
    try {
      await API.post(`/cars/${transferCar.uuid}/transfer`, { branch_id: parseInt(transferBranchId) });
      setShowTransferModal(false);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      setTransferError(err?.response?.data?.message || t("transfer_failed"));
    } finally {
      setTransferLoading(false);
    }
  };

  // تعديل الـ accessors لتطابق كائن البيانات المرسل تماماً
  const columns = [
    { header: t("brand"), accessor: "brand" },
    { header: t("model"), accessor: "model" },
    { header: t("color"), accessor: "color" },
    { header: t("chassis_number"), accessor: "chassis_number" },
    { header: t("manufacturing_year"), accessor: "manufacture_year" }, // تطابق الحقل المخزن: manufacture_year
    { header: t("plate_number"), accessor: "plate_number" },
    { header: t("license_expiry_date"), accessor: "license_expiry_date" },
    { header: t("car_price"), accessor: (row) => toLocal(row.price) }, // تطابق الحقل المخزن: price
    { header: t("status"), accessor: (row)=> row.status === "sold" ? <span style={{background:"red",padding:"2px 4px",borderRadius:"4px"}}>{t("sold")}</span> : <span style={{background:"green",padding:"2px 4px",borderRadius:"4px"}}>{t("available")}</span> },
    {
      header: t("actions"),
      accessor: (row) => (
        <div style={{ width: "fit-content" }} className="d-flex border mx-auto rounded">
          <Can permission="car_show">
            <button 
              className="btn btn-sm  mx-1 no-style" 
              onClick={() => handleView(row)}
              title={t("view")}
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
          </Can>
          <Can permission="car_show">
            <button
              className="btn btn-sm  mx-1 no-style"
              onClick={() => handleTransferOpen(row)}
              title={t("transfer_car")}
            >
              <FontAwesomeIcon icon={faRightLeft} />
            </button>
          </Can>
        </div>
      ),
      hideOnPrint: true,
    },
  ];

  // ستايل داخلي بسيط لتقسيم أقسام مودال العرض بشكل جمالي متوافق مع الـ Light & Dark Mode
  const subSectionStyle = {
    padding: "12px 12px 0 12px",
    backgroundColor: "rgba(128, 128, 128, 0.05)",
    border: "1px solid rgba(128, 128, 128, 0.12)",
    borderRadius: "6px",
    marginBottom: "10px"
  };

  return (
    <div className="container mt-5">
      <Mytitle title={t("car_inventory_report")} />

      

      {/* Cars Table */}
      <MyTable
  resource={selectedBranch ? `cars?branch_id=${selectedBranch}&ignore_page=` : "cars"}
  columns={columns}
  refreshKey={refreshKey}
  method="get"
  title={t("car_inventory_report")}
>
  {/* Branch Filter */}
      <div className="row">
        <div className="">
          <FloatingLabel label={t("branch")}>
            <Form.Select
              className="form-select"
              style={{ height: "50px" }}
              value={selectedBranch || ""}
              onChange={(e) => handleBranchChange(e.target.value)}
            >
              <option value="">{t("all_branches")}</option>
              {branches.map((branch) => (
                <option key={branch.uuid || branch.id} value={ branch.id}>
                  {branch.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </div>
      </div>
</MyTable>

      {/* View Car Details Modal */}
      <ModalForm
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={`${t("view_car_details")} - ${selectedCar?.brand || ""} ${selectedCar?.model || ""}`}
        mode="print"
        size="lg"
      >
        {selectedCar && (
          <div className="container-fluid">
  {/* القسم الأول: بيانات السيارة الأساسية (9 عناصر -> ستظهر 2 في كل سطر، والأخير بمفرده بشكل متناسق) */}
  <div style={subSectionStyle}>
    <h6 style={{fontSize:"18px",fontWeight:"bold"}} className="text-primary mb-3">
      <FontAwesomeIcon icon={faCar} className="me-2 ml-2" />
      {t("car_info")}
    </h6>
    <div className="row g-3">
      {/* تم تغيير col-md-4 إلى col-6 ليصبحوا عنصرين في السطر دائمًا */}
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("brand")}:</label>
        <p className="border-bottom pb-1">{selectedCar.brand || "-"}</p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("model")}:</label>
        <p className="border-bottom pb-1">{selectedCar.model || "-"}</p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("color")}:</label>
        <p className="border-bottom pb-1">{selectedCar.color || "-"}</p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("manufacturing_year")}:</label>
        <p className="border-bottom pb-1">{selectedCar.manufacture_year || "-"}</p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("plate_number")}:</label>
        <p className="border-bottom pb-1 fw-bold text-info">{selectedCar.plate_number || "-"}</p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("chassis_number")}:</label>
        <p className="border-bottom pb-1">{selectedCar.chassis_number || "-"}</p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("license_expiry_date")}:</label>
        <p className="border-bottom pb-1">{selectedCar.license_expiry_date || "-"}</p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("rental_status")}:</label>
        <p className="border-bottom pb-1">
          <span className={`badge ${selectedCar.rental_status === "available" ? "bg-success" : "bg-warning"}`}>
            {t(selectedCar.rental_status) || "-"}
          </span>
        </p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("status")}:</label>
        <p className="border-bottom pb-1">{t(selectedCar.status )|| "-"}</p>
      </div>
    </div>
  </div>

  {/* القسم الثاني: التفاصيل المالية للسيارة (4 عناصر -> ستظهر كسطرين متساويين، كل سطر فيه عنصرين) */}
  <div style={subSectionStyle}> 
    <h6 style={{fontSize:"18px",fontWeight:"bold"}} className="text-success mb-3">
      <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2 ml-2" />
      {t("financial_details")}
    </h6>
    <div className="row g-3">
      {/* تم تغيير col-md-3 إلى col-6 لتثبيتها عنصرين في السطر */}
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("car_price")}:</label>
        <p className="border-bottom pb-1 fw-bold text-success">{toLocal(selectedCar.price) || "-"}</p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("paid_amount")}:</label>
        <p className="border-bottom pb-1">{toLocal(selectedCar.paid) || "-"}</p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("remaining_amount")}:</label>
        <p className="border-bottom pb-1 text-danger">{toLocal(selectedCar.remaining) || "-"}</p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("payment_method")}:</label>
        <p className="border-bottom pb-1">{t(selectedCar.payment_method) || "-"}</p>
      </div>
    </div>
  </div>

  {/* القسم الثالث: الفرع والمورد (عنصرين جاهزين -> سيبقيا عنصرين بجانب بعضهما) */}
  <div style={subSectionStyle}>
    <h6 style={{fontSize:"18px",fontWeight:"bold"}} className="text-secondary mb-3">
      <FontAwesomeIcon icon={faBuilding} className="me-2 ml-2" />
      {t("branch_and_supplier_info")}
    </h6>
    <div className="row g-3">
      {/* تم تغيير col-md-6 إلى col-6 لحمايتها من الانهيار لأسفل في الطباعة */}
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("branch")}:</label>
        <p className="border-bottom pb-1">{selectedCar.branche?.name || selectedCar.branch?.name || "-"}</p>
      </div>
      <div className="col-6">
        <label className="fw-bold text-muted small">{t("name")}:</label>
        <p className="border-bottom pb-1">{selectedCar.supplier?.name || "-"}</p>
      </div>
    </div>
  </div>

  {/* القسم الرابع: عرض صورة السيارة إن وجدت */}
  {selectedCar.car_image && (
    <div className="row mt-3">
      <div className="col-12 text-center">
        <label className="fw-bold text-muted small d-block mb-2">{t("car_image")}</label>
        <img 
          src={selectedCar.car_image} 
          alt="Car" 
          className="img-fluid rounded border shadow-sm" 
          style={{ maxHeight: "200px", objectFit: "contain" }} 
        />
      </div>
    </div>
  )}
</div>
        )}
      </ModalForm>

      {/* Transfer Car Modal */}
      <ModalForm
        show={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        title={`${t("transfer_car")} - ${transferCar?.brand || ""} ${transferCar?.model || ""}`}
        onSubmit={handleTransferSubmit}
        isLoading={transferLoading}
        size="md"
      >
        <div className="p-2">
          <p className="text-muted mb-3">
            {t("select_branch_to_transfer")}
          </p>
          <FloatingLabel label={t("branch")}>
            <Form.Select
              value={transferBranchId}
              onChange={(e) => {
                setTransferBranchId(e.target.value);
                setTransferError("");
              }}
              isInvalid={!!transferError}
            >
              <option value="">{t("select_branch")}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
          {transferError && (
            <div className="text-danger mt-2 small">{transferError}</div>
          )}
        </div>
      </ModalForm>
    </div>
  );
}

export default CarInventoryReport;